// Per-delivery-form traceability page. Reached from intake — either
// the wave-in dialog before confirming arrival, or the intake detail
// page's "view delivery" button after it. Mirrors the OT traceability
// page's tabbed layout (Résumé / Producteurs / Parcelles /
// Transactions) but scopes everything to a single truckload.

import { useState } from "react"
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileText,
  LayoutDashboard,
  Lock,
  MapPin,
  Receipt,
  Truck,
  Unlock,
  Users,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"

import { BreadcrumbNav } from "@/components/breadcrumb-nav"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { DeliveryDocumentsTab } from "@/features/delivery-forms/components/delivery-documents-tab"
import { DeliveryParcelsTab } from "@/features/delivery-forms/components/delivery-parcels-tab"
import { DeliverySummaryTab } from "@/features/delivery-forms/components/delivery-summary-tab"
import { useDeliveryFormTraceability } from "@/features/delivery-forms/hooks/use-delivery-form"
import { TraceabilityProducersTab } from "@/features/transit/components/traceability/producers-tab"
import { TraceabilityTransactionsTab } from "@/features/transit/components/traceability/transactions-tab"
import { resolveApiError } from "@/lib/api/error-i18n"
import { paths } from "@/routes/paths"

export function DeliveryFormDetailPage() {
  const { deliveryFormId } = useParams<{ deliveryFormId: string }>()
  const { t } = useTranslation(["intake", "transit", "common", "documents"])
  const navigate = useNavigate()
  const query = useDeliveryFormTraceability(deliveryFormId)
  const [activeTab, setActiveTab] = useState("summary")

  if (query.isLoading || (!query.data && !query.error)) {
    return <PageSkeleton />
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex flex-col gap-3 p-6">
        <p className="text-sm text-muted-foreground">
          {query.error
            ? resolveApiError(query.error, t)
            : t("deliveryForm.notFound", { ns: "intake" })}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(paths.reception)}
          className="self-start"
        >
          <ArrowLeft className="size-4" />
          {t("deliveryForm.back", { ns: "intake" })}
        </Button>
      </div>
    )
  }

  const { deliveryForm: df, traceability } = query.data
  const digest = traceability.deliveries[0]
  const slip = df.billOfLadingNumber ?? df.shippingFormNumber ?? `#${df.id}`
  const counts = {
    producers: traceability.summary.producers.total,
    parcels: traceability.summary.parcels.total,
    transactions: traceability.summary.purchases.total,
  }

  const eudrTone = digest?.isCompliant
    ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
    : digest?.hasRisks
      ? "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
      : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
  const eudrStatus = digest?.isCompliant
    ? "PASS"
    : digest?.hasRisks
      ? "FAIL"
      : "IN_PROGRESS"

  return (
    <div className="flex flex-col gap-5 p-6">
      <BreadcrumbNav
        crumbs={[
          { label: t("title", { ns: "intake" }), to: paths.reception },
          { label: t("deliveryForm.pageTitle", { ns: "intake" }) },
        ]}
      />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {slip}
            </h1>
            <LockBadge isLocked={df.isLocked} />
          </div>
          <p className="text-sm text-muted-foreground">
            {df.cooperativeName ?? "—"}
            {df.truckRegistrationNumber ? (
              <>
                {" · "}
                <Truck className="inline size-3.5 align-text-bottom" />{" "}
                {df.truckRegistrationNumber}
              </>
            ) : null}
            {df.orderNumber ? <> · {df.orderNumber}</> : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {digest ? (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${eudrTone}`}
            >
              {digest.isCompliant ? (
                <CheckCircle2 className="size-3.5" />
              ) : (
                <AlertTriangle className="size-3.5" />
              )}
              {t(`terminer.eudrStatus.${eudrStatus}`, { ns: "transit" })}
            </span>
          ) : null}
          <Button asChild variant="ghost" size="sm">
            <Link to={paths.reception}>
              <ArrowLeft className="size-4" />
              {t("deliveryForm.back", { ns: "intake" })}
            </Link>
          </Button>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="summary">
            <LayoutDashboard className="size-4" />
            {t("trace.tabs.summary", { ns: "transit" })}
          </TabsTrigger>
          <TabsTrigger value="producers">
            <Users className="size-4" />
            {t("trace.tabs.producers", {
              ns: "transit",
              count: counts.producers,
            })}
          </TabsTrigger>
          <TabsTrigger value="parcels">
            <MapPin className="size-4" />
            {t("trace.tabs.parcels", {
              ns: "transit",
              count: counts.parcels,
            })}
          </TabsTrigger>
          <TabsTrigger value="transactions">
            <Receipt className="size-4" />
            {t("trace.tabs.transactions", {
              ns: "transit",
              count: counts.transactions,
            })}
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="size-4" />
            {t("title", { ns: "documents" })}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="pt-4">
          <DeliverySummaryTab data={query.data} />
        </TabsContent>
        <TabsContent value="producers" className="pt-4">
          <TraceabilityProducersTab traceability={traceability} />
        </TabsContent>
        <TabsContent value="parcels" className="pt-4">
          <DeliveryParcelsTab
            deliveryFormId={df.id}
            traceability={traceability}
            active={activeTab === "parcels"}
          />
        </TabsContent>
        <TabsContent value="transactions" className="pt-4">
          <TraceabilityTransactionsTab traceability={traceability} />
        </TabsContent>
        <TabsContent value="documents" className="pt-4">
          <DeliveryDocumentsTab deliveryFormId={df.id} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

function LockBadge({ isLocked }: { isLocked: boolean }) {
  const { t } = useTranslation("intake")
  if (isLocked) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
        <Lock className="size-3" />
        {t("deliveryForm.header.locked")}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
      <Unlock className="size-3" />
      {t("deliveryForm.header.unlocked")}
    </span>
  )
}

function PageSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-6">
      <Skeleton className="h-4 w-72" />
      <Skeleton className="h-8 w-96" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
      <Card className="p-5">
        <Skeleton className="h-40" />
      </Card>
    </div>
  )
}
